import { useRef } from "react";

import { noop } from "neetocist";
import useHotkeys from "neetohotkeys";
import { Modal } from "neetoui";
import PropTypes from "prop-types";
import { isEmpty } from "ramda";

import ModalBody from "./Body";
import "./finder-modal.scss";
import ModalFooter from "./Footer";
import ModalHeader from "./Header";
import { minMax } from "./utils";

const FinderModal = ({
  enableAdvancedSearch = true,
  data,
  isFetching,
  isOpen,
  searchTerm,
  setSearchTerm,
  onClose,
  onGoToAdvanced = noop,
  ...props
}) => {
  const containerRef = useRef();
  const mutables = useRef({ focusIndex: -1 }).current;

  const onFocusChange = offset => {
    const container = containerRef.current;
    if (!container) return;

    const nodes = container.querySelectorAll(
      "a.neeto-molecules-search-result-item"
    );

    if (isEmpty(nodes)) return;

    mutables.focusIndex = minMax(
      mutables.focusIndex + offset,
      nodes.length - 1
    );

    const targetNode = nodes[mutables.focusIndex];
    if (targetNode) {
      targetNode.focus();
    }
  };

  useHotkeys(
    ["up", "down"],
    event => {
      event.preventDefault();
      if (event.code === "ArrowUp") onFocusChange(-1);
      else if (event.code === "ArrowDown") onFocusChange(1);
    },
    { mode: "global", enabled: isOpen }
  );

  useHotkeys(["ctrl+enter", "command+enter"], onGoToAdvanced, {
    mode: "global",
    enabled: isOpen && enableAdvancedSearch,
  });

  return (
    <Modal
      {...{ isOpen, onClose }}
      closeButton={false}
      {...props}
      backdropClassName="neeto-molecules-finder-modal-backdrop py-10 px-4"
      className="neeto-molecules-finder-modal"
    >
      <ModalHeader
        {...{ enableAdvancedSearch, onGoToAdvanced }}
        loading={isFetching}
        onSearch={setSearchTerm}
      />
      <ModalBody
        {...{ containerRef, data, onClose, searchTerm }}
        loading={isFetching}
      />
      <ModalFooter {...{ enableAdvancedSearch }} />
    </Modal>
  );
};

FinderModal.propTypes = {
  /*
    Boolean to indicate decide whether to show advanced search button or not
  */
  enableAdvancedSearch: PropTypes.bool,
  /*
    Data to be displayed in the modal
  */
  data: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
        body: PropTypes.string,
        name: PropTypes.string,
      })
    )
  ),
  /*
    Boolean to indicate whether data is being fetched or not
  */
  isFetching: PropTypes.bool,
  /*
    Boolean to indicate whether modal is open or not
  */
  isOpen: PropTypes.bool,
  /*
    Term used for search
  */
  searchTerm: PropTypes.string,
  /*
    Function to set search term
  */
  setSearchTerm: PropTypes.func,
  /*
    Function to close the modal
  */
  onClose: PropTypes.func,
  /*
    Function to execute when user clicks on advanced search button
  */
  onGoToAdvanced: PropTypes.func,
};

export default FinderModal;
